import { useEffect, useState } from "react";
import { firebaseIdle } from "@/lib/firebase/app";
import { getDb, loadFirestoreModule } from "@/lib/firebase/db";

/**
 * Beoordelingen PER ARTIKEL, als één gecachte lijst voor de hele sessie.
 *
 * Waarom deze naast [[src/lib/reviews.ts]]: die leest de reviews van één artikel (voor het
 * reviewblok op de productpagina). Voor sterren onder productkaarten heb je van tien producten
 * tegelijk alleen het cijfer nodig — tien losse reads zou onzin zijn. De collectie
 * `productReviews` bevat alleen artikelen die daadwerkelijk beoordeeld zijn (de Kiyoh-sync
 * schrijft niets voor de rest), dus dit blijft klein.
 *
 * Geen document = geen beoordelingen = geen sterren. We tonen bij een artikel nooit het
 * winkelcijfer als vervanging: dat suggereert een productbeoordeling die er niet is.
 */

export interface ProductRating {
  /** Gemiddelde op onze 5-puntsschaal. */
  average: number;
  count: number;
}

export type RatingMap = Record<string, ProductRating>;

let cached: RatingMap | undefined;
let pending: Promise<RatingMap> | null = null;
const subscribers = new Set<(m: RatingMap) => void>();

async function readAll(): Promise<RatingMap> {
  try {
    const [{ collection, getDocs }, db] = await Promise.all([loadFirestoreModule(), getDb()]);
    const snap = await getDocs(collection(db, "productReviews"));
    const out: RatingMap = {};
    snap.forEach((d) => {
      const r = d.data() as { average?: unknown; count?: unknown };
      // Kiyoh rekent op 10, wij tonen op 5.
      const gemiddelde = Number(r.average) / 2;
      const aantal = Number(r.count);
      if (!Number.isFinite(gemiddelde) || gemiddelde <= 0 || !Number.isFinite(aantal) || aantal < 1) return;
      out[d.id] = { average: gemiddelde, count: aantal };
    });
    return out;
  } catch {
    // App Check op localhost, offline, rules — dan liever geen sterren dan verkeerde sterren.
    return {};
  }
}

function loadOnce(): Promise<RatingMap> {
  if (cached !== undefined) return Promise.resolve(cached);
  pending ??= firebaseIdle()
    .then(readAll)
    .then((m) => {
      pending = null;
      cached = m;
      subscribers.forEach((fn) => fn(m));
      return m;
    });
  return pending;
}

/** Hook: {slug → cijfer}. Leeg zolang er nog niets geladen is. */
export function useProductRatings(): RatingMap {
  const [map, setMap] = useState<RatingMap>(cached ?? {});
  useEffect(() => {
    if (cached !== undefined) { setMap(cached); return; }
    let alive = true;
    const fn = (m: RatingMap) => { if (alive) setMap(m); };
    subscribers.add(fn);
    void loadOnce();
    return () => { alive = false; subscribers.delete(fn); };
  }, []);
  return map;
}
